import { CITIES } from "@/lib/cities";

/**
 * Blog data: one post per major Florida city — the 12 service-area cities plus
 * the next 20 largest Florida cities by population. Facts kept general and safe;
 * math examples are illustrative arithmetic, never quotes.
 */
const REGION_BY_SLUG = {
  miami: "Southeast Florida",
  tampa: "Tampa Bay",
  orlando: "Central Florida",
  jacksonville: "Northeast Florida",
  "fort-lauderdale": "Southeast Florida",
  "st-petersburg": "Tampa Bay",
  sarasota: "Southwest Florida",
  "fort-myers": "Southwest Florida",
  naples: "Southwest Florida",
  "west-palm-beach": "Southeast Florida",
  melbourne: "Space Coast",
  "daytona-beach": "East Coast",
};

const EXAMPLE_BILL_BY_SLUG = {
  miami: 12500, tampa: 12500, orlando: 12500, jacksonville: 12500, hialeah: 12500, tallahassee: 10000,
  "port-st-lucie": 9000, "cape-coral": 9000, "pembroke-pines": 9000, hollywood: 9000, clearwater: 9000,
  lakeland: 9000, gainesville: 9000, "fort-lauderdale": 10000, "st-petersburg": 10000, "west-palm-beach": 10000,
  sarasota: 7500, "fort-myers": 7500, naples: 7500, melbourne: 7500, "daytona-beach": 6500, "palm-bay": 7500,
  "palm-coast": 6500, davie: 7500, "miami-gardens": 9000, sunrise: 7500, plantation: 7500,
  "pompano-beach": 6500, deltona: 6500, homestead: 6500, "coral-springs": 7500, miramar: 7500,
};

const EXTRA_CITIES = [
  {
    slug: "hialeah", name: "Hialeah", county: "Miami-Dade County", region: "Southeast Florida",
    utilities: "FPL serves Hialeah.",
    blurb:
      "One of Florida's densest cities, Hialeah runs on small manufacturing, distribution and family-owned retail — long daytime hours that match solar production well. Its stock of flat-roofed warehouses and plazas gives commercial arrays plenty of usable area.",
    lat: 25.8576, lng: -80.2781, mapQuery: "Hialeah, FL",
  },
  {
    slug: "tallahassee", name: "Tallahassee", county: "Leon County", region: "Panhandle",
    utilities: "The City of Tallahassee's municipal utility serves the city.",
    blurb:
      "Florida's capital pairs government offices, university facilities and healthcare with its own municipal utility. Cooler panhandle winters and strong spring sun shape a production profile that rewards a full-year bill review rather than a one-month snapshot.",
    lat: 30.4383, lng: -84.2807, mapQuery: "Tallahassee, FL",
  },
  {
    slug: "port-st-lucie", name: "Port St. Lucie", county: "St. Lucie County", region: "Treasure Coast",
    utilities: "FPL serves Port St. Lucie.",
    blurb:
      "One of the fastest-growing cities in the United States, Port St. Lucie mixes new construction, retirement services and light industry along the Treasure Coast. Young roof stock means fewer re-roofing conflicts for solar projects here.",
    lat: 27.2731, lng: -80.3583, mapQuery: "Port St. Lucie, FL",
  },
  {
    slug: "cape-coral", name: "Cape Coral", county: "Lee County", region: "Southwest Florida",
    utilities: "LCEC, the local electric cooperative, serves Cape Coral.",
    blurb:
      "Cape Coral's canal-lined neighborhoods and commercial corridors run on LCEC. The city's rapid growth has left businesses with rising bills — and the flat, sun-exposed roofs that commercial solar uses best.",
    lat: 26.5629, lng: -81.9495, mapQuery: "Cape Coral, FL",
  },
  {
    slug: "pembroke-pines", name: "Pembroke Pines", county: "Broward County", region: "Southeast Florida",
    utilities: "FPL serves Pembroke Pines.",
    blurb:
      "A major Broward hub of retail, medical and office space, Pembroke Pines runs on FPL power. Its hurricane exposure is exactly why wind-rated engineering is the local baseline, not an upgrade.",
    lat: 26.0112, lng: -80.3501, mapQuery: "Pembroke Pines, FL",
  },
  {
    slug: "hollywood", name: "Hollywood", county: "Broward County", region: "Southeast Florida",
    utilities: "FPL serves Hollywood.",
    blurb:
      "Hollywood's beachfront hospitality runs hard during season, while FPL serves the city year-round. Long dry-season stretches keep rooftop production strong straight through winter.",
    lat: 26.0112, lng: -80.1494, mapQuery: "Hollywood, FL",
  },
  {
    slug: "miramar", name: "Miramar", county: "Broward County", region: "Southeast Florida",
    utilities: "FPL serves Miramar.",
    blurb:
      "Miramar sits between Miami and Broward's logistics corridors with a business-park economy of distribution, tech and offices — daytime-heavy loads that line up well with solar production.",
    lat: 25.9873, lng: -80.3042, mapQuery: "Miramar, FL",
  },
  {
    slug: "gainesville", name: "Gainesville", county: "Alachua County", region: "North Central Florida",
    utilities: "GRU (Gainesville Regional Utilities) serves the city.",
    blurb:
      "Home to the University of Florida, Gainesville runs on the GRU municipal utility with institutional, healthcare and rental-housing loads. North Florida's cooler winters shift the production curve — not the math.",
    lat: 29.6516, lng: -82.3248, mapQuery: "Gainesville, FL",
  },
  {
    slug: "coral-springs", name: "Coral Springs", county: "Broward County", region: "Southeast Florida",
    utilities: "FPL serves Coral Springs.",
    blurb:
      "A planned city of plazas, offices and medical space in northwest Broward, served by FPL. Strong coastal sun and steady year-round demand make a bill-based review straightforward here.",
    lat: 26.2712, lng: -80.2706, mapQuery: "Coral Springs, FL",
  },
  {
    slug: "clearwater", name: "Clearwater", county: "Pinellas County", region: "Tampa Bay",
    utilities: "Duke Energy serves Clearwater.",
    blurb:
      "Clearwater's hospitality and marine trades run along the Pinellas coast on Duke Energy power. Tampa Bay's lightning-prone summers make surge-aware design and storage conversations natural here.",
    lat: 27.9659, lng: -82.8001, mapQuery: "Clearwater, FL",
  },
  {
    slug: "palm-bay", name: "Palm Bay", county: "Brevard County", region: "Space Coast",
    utilities: "FPL serves Palm Bay.",
    blurb:
      "Palm Bay anchors the Space Coast's south end with light manufacturing, aerospace suppliers and growing retail. Large flat commercial roofs and steady technical loads suit solar well.",
    lat: 27.9986, lng: -80.6706, mapQuery: "Palm Bay, FL",
  },
  {
    slug: "lakeland", name: "Lakeland", county: "Polk County", region: "Central Florida",
    utilities: "Lakeland Electric, the municipal utility, serves the city.",
    blurb:
      "Between Tampa and Orlando, Lakeland runs on Lakeland Electric with distribution centers and food processing among its biggest loads. Long summer days support peak production right when cooling demand peaks.",
    lat: 28.0395, lng: -81.9498, mapQuery: "Lakeland, FL",
  },
  {
    slug: "palm-coast", name: "Palm Coast", county: "Flagler County", region: "Northeast Florida",
    utilities: "FPL serves Palm Coast.",
    blurb:
      "A planned coastal community south of St. Augustine, Palm Coast runs on FPL with a growing base of medical and professional offices. Flagler County's coastal sun keeps production steady through the dry season.",
    lat: 29.5844, lng: -81.2078, mapQuery: "Palm Coast, FL",
  },
  {
    slug: "davie", name: "Davie", county: "Broward County", region: "Southeast Florida",
    utilities: "FPL serves Davie.",
    blurb:
      "Davie's ranch lands and commerce parks give west Broward an unusual mix — equestrian facilities, retail and logistics on FPL power. Flat commercial roofs dominate the corridors along I-595.",
    lat: 26.0629, lng: -80.2398, mapQuery: "Davie, FL",
  },
  {
    slug: "miami-gardens", name: "Miami Gardens", county: "Miami-Dade County", region: "Southeast Florida",
    utilities: "FPL serves Miami Gardens.",
    blurb:
      "One of Miami-Dade's largest cities, Miami Gardens runs on FPL power with stadium events, retail and education loads. Big, unshaded flat roofs are common along its commercial corridors.",
    lat: 25.9424, lng: -80.2455, mapQuery: "Miami Gardens, FL",
  },
  {
    slug: "sunrise", name: "Sunrise", county: "Broward County", region: "Southeast Florida",
    utilities: "FPL serves Sunrise.",
    blurb:
      "Sunrise hosts the Sawgrass corporate corridor — offices, retail and logistics west of Fort Lauderdale, on FPL power. Daytime-heavy commercial loads align well with solar production.",
    lat: 26.1345, lng: -80.3031, mapQuery: "Sunrise, FL",
  },
  {
    slug: "plantation", name: "Plantation", county: "Broward County", region: "Southeast Florida",
    utilities: "FPL serves Plantation.",
    blurb:
      "Plantation's office parks and medical centers anchor central Broward on FPL power. Mature tree canopy makes a roof-by-roof shade review worth the time here.",
    lat: 26.1276, lng: -80.2331, mapQuery: "Plantation, FL",
  },
  {
    slug: "pompano-beach", name: "Pompano Beach", county: "Broward County", region: "Southeast Florida",
    utilities: "FPL serves Pompano Beach.",
    blurb:
      "Pompano Beach pairs marine industries and hospitality with a growing innovation district, served by FPL. Coastal salt air is a design input, not a blocker — marine-rated equipment is standard.",
    lat: 26.2379, lng: -80.1247, mapQuery: "Pompano Beach, FL",
  },
  {
    slug: "deltona", name: "Deltona", county: "Volusia County", region: "East Coast",
    utilities: "FPL serves Deltona.",
    blurb:
      "Deltona spreads across southwest Volusia with commuter, retail and healthcare loads on FPL power. Its I-4 corridor position ties it directly to the Orlando logistics market.",
    lat: 28.9005, lng: -81.2637, mapQuery: "Deltona, FL",
  },
  {
    slug: "homestead", name: "Homestead", county: "Miami-Dade County", region: "South Miami-Dade",
    utilities: "FPL serves most of Homestead; the City of Homestead's municipal utility serves parts of the city.",
    blurb:
      "Homestead anchors south Miami-Dade with agriculture, logistics and gateway traffic to the Keys. Check your bill header — the serving utility changes the tariff math, not the opportunity.",
    lat: 25.4687, lng: -80.4777, mapQuery: "Homestead, FL",
  },
];

const SPECIAL_POSTS = [
  {
    slug: "now-hiring-all-solar-positions-open",
    special: "careers",
    city: "Statewide — Florida",
    county: "Fort Myers · Naples · Miami",
    region: "Careers",
    utilities: "All positions are full-time with the licensed installation provider's crews.",
    blurb:
      "Now hiring across Florida: solar PV installers, solar electricians, electrical apprentices, metal roofing technicians, HVAC technicians and solar service technicians. Veteran and women owned, operating since 2019, with solar, roofing, electrical and HVAC trades under one roof. Every position has an application form with resume upload.",
    lat: 0, lng: 0, mapQuery: "Florida",
  },
];

export const BLOG_POSTS = [
  ...CITIES.map((c) => ({
    slug: `solar-${c.slug}`,
    city: c.name,
    county: c.county,
    region: REGION_BY_SLUG[c.slug] || "Florida",
    utilities: c.utilities,
    blurb: c.blurb,
    lat: c.lat,
    lng: c.lng,
    mapQuery: c.mapQuery,
  })),
  ...EXTRA_CITIES.map((c) => ({
    slug: `solar-${c.slug}`,
    city: c.name,
    county: c.county,
    region: c.region,
    utilities: c.utilities,
    blurb: c.blurb,
    lat: c.lat,
    lng: c.lng,
    mapQuery: c.mapQuery,
  })),
].map((p) => ({ ...p, exampleBill: EXAMPLE_BILL_BY_SLUG[p.slug.replace("solar-", "")] || 9000 })).concat(SPECIAL_POSTS);

export function findPost(slug) {
  return BLOG_POSTS.find((p) => p.slug === slug) || null;
}
